

import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AppErrorMessageEvent, AppInfoMessageEvent, AppSuccessMessageEvent, AppWarningMessageEvent } from './app-events';
//import { AppCache } from './app-cache'
import { AppSettings } from './app-settings';
import { Stage, User } from './models';
import { ShowRequest } from './show-request';
import { WebSocketSubject } from 'rxjs/webSocket';

export type TestbedServerJob = {
    key: any;
    title: string;
    type: 'correlate'|'rebuildCorrelationDashboard'|'reportGeneration';
    startTime: Date;
    status: {
        value: 'initialized'|'running'|'complete'|'error';
        description?: string;
    };
    downloadUrl?: string;
};

export type DbEntityChange = {
    id: string|number;
    modifiedAt: Date;
    revisionId?: number
    volunteerId?: string; // Check flyway R__9.11Notify_volunteer.sql (currently everything returns volunteerId)
};

export type DbEntityChangedEvent = {
    type: string;
    channel: 'volunteer_change'|'volunteer_point_change'|'volunteer_task_point_change'
            |'volunteer_task_change'|'volunteer_task_series_change'|'volunteer_call_rut_change'
            |'volunteer_call_photo_change';
    payload: DbEntityChange;
};

type UserConnection = {
    userId: string;
    email: string;
    clientIp: string;
    connectTime: Date;
};

export type TestbedServerClientStats = {
    connectedClients: number,
    connectedWebUsers: Array<UserConnection>,
    connectedMobileUsers: Array<UserConnection>,
};

export const sameRevision = ( changeEvent: DbEntityChangedEvent, revisionId?: string|number) => {
    const bSame = changeEvent.payload.revisionId === revisionId ||
    (revisionId != null && `${changeEvent.payload.revisionId}` === `${revisionId}`);
    console.log(`sameRevision: ${changeEvent.payload.revisionId} === ${revisionId} = ${bSame}`);
    return bSame
}

@Injectable({
  providedIn: 'root',
})
export class AppState {

    settings = new AppSettings();
    dbEntityChange$ = new EventEmitter<DbEntityChangedEvent>();
    clientStats$ = new BehaviorSubject<TestbedServerClientStats|null>(null);

    webSocketSubject: WebSocketSubject<any>;
    webSocketMsg$ = new EventEmitter<{type:'appState'|'jobEvent'|'activeJobs'|'dbEntityChanged',data: any}>();

    userSubject = new BehaviorSubject<User>(null)

    isOfflineSubject = new BehaviorSubject<boolean>(true)

    pendingUploadCountSubject = new BehaviorSubject<number>(0)

    readySubject = new BehaviorSubject<boolean>(false)

    appExitedSubject = new BehaviorSubject<boolean>(false)
    appExitedMsg : string

    activeStage: string

    errorEmitter = new EventEmitter<AppErrorMessageEvent>()
    successEmitter = new EventEmitter<AppSuccessMessageEvent>()
    infoEmitter = new EventEmitter<AppInfoMessageEvent>()

    notifyErrorEmitter = new EventEmitter<AppErrorMessageEvent>()
    notifyWarningEmitter = new EventEmitter<AppWarningMessageEvent>()
    notifySuccessEmitter = new EventEmitter<AppSuccessMessageEvent>()
    notifyInfoEmitter = new EventEmitter<AppInfoMessageEvent>()

    showView = new Subject<ShowRequest>()

    constructor(){
        this.activeStage = localStorage.getItem('preferences.stage')
        if ( this.activeStage ) {
            Stage.DefaultStageName = this.activeStage
        }
    }

    get isReady() {
        return this.readySubject.value
    }

    isOffline() {
        return this.isOfflineSubject.value
    }

    setOffline( bOffline : boolean ) {
        this.isOfflineSubject.next(bOffline)
    }

    getPendingUploadCount() {
        return this.pendingUploadCountSubject.value
    }

    setPendingUploadCount( cnt : number ) {
        this.pendingUploadCountSubject.next(cnt)
    }

    getUser() {
        return this.userSubject.value
    }

    setUser( user : User ) {
        this.userSubject.next(user)
    }

    isElectron(): boolean {
        if (typeof window !== 'undefined' && typeof window.process === 'object' && window.process.type === 'renderer') {
            return true;
        }
        if (typeof process !== 'undefined' && typeof process.versions === 'object' && !!process.versions['electron']) {
            return true;
        }
        if (typeof navigator === 'object' && typeof navigator.userAgent === 'string' && navigator.userAgent.indexOf('Electron') >= 0) {
            return true;
        }
    }

    trackWebSocketReply( msg) {
        if ( this.isOffline() ) {
            this.setOffline(false)
            console.log('appState.trackWebSocketReply setOffline to ' + this.isOffline())
        }

        if ( !this.isReady ) {
            this.publishInfo('Connection established',this,10)
            this.setWebSocketCheckComplete(true)
        }
    }

    trackWebSocketError( error ) {
        if ( !this.isOffline() ) {
            this.setOffline(true)
            console.log('appState.trackWebSocketError setOffline to ' + this.isOffline())
        }

        if ( !this.isReady ) {
            this.publishInfo('Switched to offline mode',this,10)
            this.setWebSocketCheckComplete(true)
        }
    }

    publishError( error : any, source : any = null, expireSecs : number = AppErrorMessageEvent.DEFAULT_EXPIRE_SECS) {
        var errMsg = error
        if (typeof error === 'string' || error instanceof String ) {
            error = new Error(source?.constructor['name'] || typeof source)
        } else {
            errMsg = `${error?.message||error}`
        }
        this.publishErrorObj(error,errMsg,source,expireSecs)
        return errMsg
    }

    publishErrorObj(error: any, title: string, source: any, expireSecs = AppErrorMessageEvent.DEFAULT_EXPIRE_SECS): string {
        let errEvent = new AppErrorMessageEvent(title, source, expireSecs)
        if ( !error?.stack ) {
            errEvent.stack = new Error(source?.constructor['name'] || typeof source).stack
        }
        errEvent.error = error
        this.errorEmitter.next(errEvent)
        return title
    }

    publishSuccess( msg : string, source : any = null, expireSecs : number = AppSuccessMessageEvent.DEFAULT_EXPIRE_SECS) {
        this.successEmitter.next( new AppSuccessMessageEvent(msg,source?source:this,expireSecs))
    }
    publishInfo( msg : string, source : any = null, expireSecs : number = AppInfoMessageEvent.DEFAULT_EXPIRE_SECS) {
        this.infoEmitter.next( new AppInfoMessageEvent(msg,source?source:this,expireSecs))
    }


    notifyError( msg : string, source : any = null, expireSecs : number) {
        this.notifyErrorEmitter.next( new AppErrorMessageEvent(msg,source?source:this,expireSecs))
    }
    notifyWarning( msg : string, source : any = null, expireSecs : number) {
        this.notifyWarningEmitter.next( new AppWarningMessageEvent(msg,source?source:this,expireSecs))
    }
    notifySuccess( msg : string, source : any = null, expireSecs : number) {
        this.notifySuccessEmitter.next( new AppSuccessMessageEvent(msg,source?source:this,expireSecs))
    }
    notifyInfo( msg : string, source : any = null, expireSecs : number) {
        this.notifyInfoEmitter.next( new AppInfoMessageEvent(msg,source?source:this,expireSecs))
    }


    // HomeComponent swaps out this function for one that checks for unsaved work
    confirmExit = ( exitFn : ()=>void, noExitFn : ()=>void = () =>{} ) => {
        // Default is do not prompt user and just call exitFn
        console.log('AppState.confirmExit...')
        exitFn()
    }

    doWhenReady( readyFn : () => void ) {
        return this.readySubject.pipe(filter(bReady=>bReady), take(1)).subscribe( bReady => {
            if ( bReady ) {
                readyFn()
            }
        })
    }

    private bRxdbMgrInitialized = false
    setRxdbMgrInitialized( bInitialized : boolean ) {
        this.bRxdbMgrInitialized = true
        this.decideIfReady()
    }

    private bWebSocketCheckComplete = false
    setWebSocketCheckComplete( bInitialized : boolean ) {
        this.bWebSocketCheckComplete = true
        this.decideIfReady()
    }

    private decideIfReady() {
        if ( !this.isReady && this.bRxdbMgrInitialized && this.bWebSocketCheckComplete ) {
            console.log('Application started (ready)')
            this.readySubject.next(true)
        }
    }

}